<template>
  <section :id="`year-${year.year}`" class="year-section" :class="{ alternate: index % 2 === 1, upcoming: year.status === 'upcoming' }">
    <v-container>
      <header class="year-heading">
        <div>
          <p>{{ year.status === 'upcoming' ? 'Upcoming Chapter' : 'The re:Invent Story' }}</p>
          <h2>{{ year.year }}</h2>
        </div>
        <span v-if="year.dates">{{ year.dates }}</span>
        <span v-else-if="year.status !== 'upcoming'">{{ attendeeCount }} attendees</span>
      </header>

      <div v-if="year.status === 'upcoming'" class="upcoming-copy">
        The next chapter is still being written.
      </div>

      <div v-else class="year-content">
        <div v-if="year.highlights && year.highlights.length" class="year-block highlights-block">
          <h3>Highlights</h3>
          <ul>
            <li v-for="highlight in year.highlights" :key="highlight">{{ highlight }}</li>
          </ul>
        </div>
        <div v-if="year.attendees && year.attendees.length" class="year-block">
          <h3>Crew</h3>
          <div class="pill-list">
            <component
              :is="attendeeRoute(attendee) ? 'router-link' : 'span'"
              v-for="attendee in year.attendees"
              :key="attendee"
              :to="attendeeRoute(attendee)"
            >
              {{ attendee }}
            </component>
          </div>
        </div>
        <div v-if="year.activities && year.activities.length" class="year-block">
          <h3>Activities</h3>
          <ul class="compact-list">
            <li v-for="activity in year.activities" :key="activity">
              <router-link :to="activityRoute(activity)">{{ activity }}</router-link>
            </li>
          </ul>
        </div>
        <div v-if="year.restaurants && year.restaurants.length" class="year-block">
          <h3>Restaurants</h3>
          <ul class="compact-list">
            <li v-for="restaurant in year.restaurants" :key="restaurant">
              <router-link :to="restaurantRoute(restaurant)">{{ restaurant }}</router-link>
            </li>
          </ul>
        </div>
      </div>
    </v-container>
  </section>
</template>

<script>
import {
  eventEntityRoute,
  normalizeEntityKey,
  resolveEventEntityDetails,
} from '../../../lib/reckon-way/entityDetails';

export default {
  name: 'RecurringEventYearSection',
  props: {
    year: { type: Object, required: true },
    index: { type: Number, default: 0 },
    event: { type: Object, required: true },
  },
  computed: {
    attendeeCount() {
      return (this.year.attendees || []).length;
    },
  },
  methods: {
    attendeeRoute(attendee) {
      const member = (this.event.recurringCrew || []).find((person) => (
        person.name === attendee || person.displayName === attendee
      ));

      return member?.slug
        ? { name: 'passport', params: { employeeSlug: member.slug } }
        : null;
    },
    activityRoute(title) {
      const activity = (this.event.activities || []).find((item) => (
        item.title === title && item.year === this.year.year
      ));

      return activity
        ? eventEntityRoute(this.event, 'activities', activity)
        : { hash: '#activities' };
    },
    restaurantRoute(name) {
      const restaurant = (this.event.restaurantVisits || []).find((item) => item.name === name);
      if (!restaurant) return { hash: `#restaurant-${normalizeEntityKey(name)}` };

      const details = resolveEventEntityDetails({
        event: this.event,
        sectionKey: 'dining',
        item: restaurant,
      });

      return details?.media.length
        ? eventEntityRoute(this.event, 'dining', restaurant)
        : { hash: `#restaurant-${normalizeEntityKey(name)}` };
    },
  },
};
</script>

<style scoped>
.year-section { background: #080808; border-top: 1px solid #242424; padding: 76px 0; scroll-margin-top: 150px; }
.year-section.alternate { background: linear-gradient(145deg, #130f09, #080808 62%); }
.year-heading { align-items: end; display: flex; justify-content: space-between; margin-bottom: 32px; }
.year-heading p { color: #e23232; font-family: "Oswald", sans-serif; font-weight: 800; letter-spacing: 0.1em; margin: 0 0 6px; text-transform: uppercase; }
.year-heading h2 { color: #fff; font-family: "Oswald", sans-serif; font-size: clamp(3.6rem, 10vw, 7rem); line-height: 0.82; margin: 0; }
.year-heading > span { color: #f0b13c; font-family: "Oswald", sans-serif; font-weight: 800; text-transform: uppercase; }
.year-content { display: grid; gap: 16px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.year-block { background: rgba(255, 255, 255, 0.035); border: 1px solid #302d29; border-radius: 12px; padding: 24px; }
.year-block h3 { color: #fff; font-family: "Oswald", sans-serif; font-size: 1.35rem; margin: 0 0 15px; }
.year-block ul { color: #c8c8c8; line-height: 1.6; margin: 0; padding-left: 20px; }
.year-block li + li { margin-top: 7px; }
.highlights-block { border-color: #51401f; }
.pill-list { display: flex; flex-wrap: wrap; gap: 8px; }
.pill-list a, .pill-list span { background: #181818; border: 1px solid #3a3a3a; border-radius: 999px; color: #ddd; padding: 7px 12px; text-decoration: none; }
.pill-list a:hover, .pill-list a:focus-visible { border-color: #f0b13c; color: #fff; outline: none; }
.compact-list { list-style: none; padding: 0 !important; }
.compact-list li { border-left: 2px solid #f0b13c; padding-left: 12px; }
.compact-list a { color: #c8c8c8; display: inline-block; text-decoration: none; }
.compact-list a:hover, .compact-list a:focus-visible { color: #f0b13c; outline: none; text-decoration: underline; text-underline-offset: 3px; }
.upcoming-copy { border: 1px dashed #5a492d; border-radius: 12px; color: #ccc; font-size: 1.2rem; padding: 32px; }
@media (max-width: 700px) { .year-content { grid-template-columns: 1fr; } .year-heading { align-items: flex-start; flex-direction: column; gap: 16px; } }
</style>
